<script setup lang="ts">
import { computed, ref, watch } from "vue"
import { CloudArrowUpIcon, FolderOpenIcon, LinkIcon } from "@heroicons/vue/24/outline"
import {
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogRoot,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "reka-ui"
import { UploadPhase } from "./Uploads"
import UploadMasterRow from "./UploadMasterRow.vue"
import { notifyError } from "../notifications"

type Filter = "all" | "running" | "errors"

interface Item {
  id: string
  key?: string
  ready?: boolean
  filename: string
  preview?: string
  phase: UploadPhase
  progress?: number
  selected: boolean
  active: boolean
  dirty: boolean
  error?: string
}

const props = defineProps<{
  items: Item[]
}>()

const emit = defineEmits<{
  "toggle-select": [id: string]
  "activate": [id: string]
  "toggle-all": [selected: boolean]
  "files-dropped": [files: FileList]
  "urls-imported": [urls: URL[]]
}>()

const filter = ref<Filter>("all")
const fileInput = ref<HTMLInputElement>()
const importUrlsRaw = ref("")
const importUrls = ref<URL[]>([])

const filteredItems = computed<Item[]>(() => {
  switch (filter.value) {
    case "running":
      return props.items.filter(i =>
        i.phase === UploadPhase.PREPARING
        || i.phase === UploadPhase.CREATING
        || i.phase === UploadPhase.UPLOADING
      )
    case "errors":
      return props.items.filter(i => i.phase === UploadPhase.FAILED)
    default:
      return props.items
  }
})

const selectableItems = computed(() => props.items.filter(i => i.ready !== false))
const allSelected = computed(() => selectableItems.value.length > 0 && selectableItems.value.every(i => i.selected))

function onDrop(event: DragEvent): void {
  event.preventDefault()
  const files = event.dataTransfer?.files
  if (files && files.length) emit("files-dropped", files)
}

function openFilePicker(): void {
  fileInput.value?.click()
}

function onFilePicked(event: Event): void {
  const target = event.target as HTMLInputElement
  if (target.files && target.files.length) emit("files-dropped", target.files)
  target.value = ""
}

watch(importUrlsRaw, (raw) => {
  const lines = raw.split("\n").map(l => l.trim()).filter(l => l.length > 0)
  if (!lines.length) return

  const parsed: URL[] = []
  for (const line of lines) {
    try {
      parsed.push(new URL(line))
    } catch {
      notifyError({ description: `Ungültige URL: ${line}` })
    }
  }
  if (parsed.length) importUrls.value.push(...parsed)
  importUrlsRaw.value = ""
})

function submitUrls(): void {
  if (!importUrls.value.length) return
  emit("urls-imported", [...importUrls.value])
  importUrls.value = []
}

function removeUrl(url: URL): void {
  importUrls.value = importUrls.value.filter(u => u.href !== url.href)
}
</script>

<template>
  <div class="flex flex-col h-full">
    <div
      data-testid="drop-zone"
      class="m-3 p-6 border-4 border-dashed border-gray-300 rounded-lg flex flex-col items-center text-sm text-gray-500 gap-3"
      @dragover.prevent
      @drop="onDrop"
    >
      <CloudArrowUpIcon class="w-10 h-10"/>
      <span>Datei hier ablegen</span>

      <div class="flex items-center gap-2">
        <button
          type="button"
          data-testid="file-picker-button"
          class="btn btn-sm"
          @click="openFilePicker"
        >
          <FolderOpenIcon class="w-4 h-4"/>
          Auswählen
        </button>

        <AlertDialogRoot>
          <AlertDialogTrigger as-child>
            <button
              type="button"
              data-testid="url-import-button"
              class="btn btn-sm"
            >
              <LinkIcon class="w-4 h-4"/>
              URL importieren
            </button>
          </AlertDialogTrigger>
          <AlertDialogPortal>
            <AlertDialogOverlay class="bg-black/75 fixed inset-0 z-40"/>
            <AlertDialogContent
              data-testid="url-import-dialog"
              class="z-[100] fixed top-1/2 left-1/2 max-h-[85vh] w-[90vw] max-w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-md bg-white p-6 shadow-xl focus:outline-none"
            >
              <AlertDialogTitle class="m-0 font-semibold">URLs importieren</AlertDialogTitle>
              <AlertDialogDescription class="mt-4 mb-5 leading-normal">
                <ul v-if="importUrls.length" class="mb-3 space-y-1">
                  <li
                    v-for="url of importUrls"
                    :key="url.href"
                    class="flex items-center gap-2 text-sm"
                  >
                    <a :href="url.href" target="_blank" class="truncate flex-1" :title="url.href">{{ url.href }}</a>
                    <button
                      type="button"
                      class="btn btn-xs btn-ghost"
                      @click="removeUrl(url)"
                    >×</button>
                  </li>
                </ul>

                <textarea
                  data-testid="url-import-textarea"
                  v-model="importUrlsRaw"
                  class="textarea textarea-bordered w-full"
                  rows="4"
                  placeholder="URLs zum Importieren — eine URL pro Zeile"
                ></textarea>
              </AlertDialogDescription>
              <div class="flex justify-end gap-3">
                <AlertDialogCancel class="btn btn-sm" @click="importUrls = []; importUrlsRaw = ''">
                  Abbrechen
                </AlertDialogCancel>
                <AlertDialogAction
                  data-testid="url-import-submit"
                  class="btn btn-sm btn-primary"
                  :disabled="!importUrls.length"
                  @click="submitUrls"
                >
                  {{ importUrls.length }} URL{{ importUrls.length === 1 ? "" : "s" }} importieren
                </AlertDialogAction>
              </div>
            </AlertDialogContent>
          </AlertDialogPortal>
        </AlertDialogRoot>
      </div>

      <input
        ref="fileInput"
        data-testid="file-picker-input"
        type="file"
        multiple
        accept="image/jpeg,image/gif,image/png,image/webp,image/svg+xml,application/pdf,.pdf,video/mp4"
        class="hidden"
        @change="onFilePicked"
      >
    </div>

    <div class="px-3 pb-2 flex items-center gap-3">
      <label class="flex items-center gap-2 text-sm">
        <input
          data-testid="all-select"
          type="checkbox"
          class="checkbox checkbox-sm"
          :checked="allSelected"
          @change="emit('toggle-all', ($event.target as HTMLInputElement).checked)"
        >
        <span>Alle auswählen</span>
      </label>
      <select
        data-testid="filter-select"
        v-model="filter"
        class="select select-sm select-bordered ml-auto"
      >
        <option value="all">Alle</option>
        <option value="running">Laufend</option>
        <option value="errors">Fehler</option>
      </select>
    </div>

    <div class="flex-1 overflow-y-auto overflow-x-hidden min-w-0">
      <UploadMasterRow
        v-for="item of filteredItems"
        :key="item.key ?? item.id"
        v-bind="item"
        @toggle-select="(id) => emit('toggle-select', id)"
        @activate="(id) => emit('activate', id)"
      />
    </div>
  </div>
</template>
